import mixpanel from "mixpanel-browser";

import { User } from "../types/api";
import { isMobileAppRuntime } from "./isMobileAppRuntime";
import {
  initTiktokPixel,
  resetTiktokPixel,
  trackTiktokPixelEvent,
} from "./tiktokPixel";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

/** Only run Mixpanel in production bundles (`yarn build`), not in local dev (`yarn start`). */
const isMixpanelRuntimeEnabled = process.env.NODE_ENV === "production";

let isInitialized = false;

/** Initialize the Mixpanel SDK once (production only; anonymous + identified users). */
export const ensureMixpanelSdk = (): boolean => {
  if (!isMixpanelRuntimeEnabled) {
    return false;
  }
  if (!MIXPANEL_TOKEN) {
    return false;
  }
  if (isInitialized) {
    return true;
  }
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: false,
    track_pageview: false,
    persistence: "localStorage",
  });
  isInitialized = true;
  return true;
};

/**
 * Identify a logged-in user (production only; call after login / session restore).
 * Anonymous sessions only need {@link ensureMixpanelSdk} via {@link trackEvent}.
 */
export const initMixpanel = (user: User | null): void => {
  if (!isMixpanelRuntimeEnabled) {
    return;
  }
  if (!ensureMixpanelSdk()) {
    return;
  }

  if (!user) {
    return;
  }

  mixpanel.identify(user.id.toString());
  mixpanel.people.set({
    $email: user.email,
    $name: user.username,
    username: user.username,
    first_name: user.first_name,
    last_name: user.last_name,
    available_credits: user.available_credits,
    no_restrictions: user.no_restrictions,
  });

  initTiktokPixel(user);
};

/**
 * Track an event when the SDK is configured (production only; no-op in development).
 *
 * Every event automatically includes:
 * - `is_mobile_app` — `true` when `window.IS_MOBILE_APP` is set by the native WebView
 * - `timestamp` — ISO time of the track call
 *
 * See `docs/mixpanel-events.md` for a catalog of event names and typical properties.
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
): void => {
  if (!isMixpanelRuntimeEnabled) {
    return;
  }
  if (!ensureMixpanelSdk()) {
    return;
  }

  mixpanel.track(eventName, {
    ...(properties ?? {}),
    is_mobile_app: isMobileAppRuntime(),
    timestamp: new Date().toISOString(),
  });

  trackTiktokPixelEvent(eventName, properties ?? {});
};

/** Payload for {@link MixpanelEvents.PARTY_GAME_STARTED}. */
export type PartyGameStartedPayload = {
  /** Party shell id: imposter, guess, act, who, fast, category, challenge, bluff. */
  shell_id: string;
  /** Selected topic/category names (not numeric IDs). */
  category_names: string[];
  /** Headcount when meaningful (roster); for two-team modes may be 2; for Answer Bluff = host + joined guests. */
  number_of_players: number;
  trial: boolean;
  /** English display name for the mode (optional). */
  mode_name_en?: string;
  imposter_count?: number;
  team_a_name?: string;
  team_b_name?: string;
  dare_mode?: "solo" | "teams";
  /** dare: which deck shape the API returned */
  dare_deck_kind?: "dares" | "truths" | "lies" | "mixed";
  seconds_per_turn?: number | null;
  deck_size?: number;
  guest_join_count?: number;
  room_code_present?: boolean;
};

export function trackPartyGameStarted(payload: PartyGameStartedPayload): void {
  trackEvent(MixpanelEvents.PARTY_GAME_STARTED, {
    shell_id: payload.shell_id,
    category_names: payload.category_names,
    number_of_players: payload.number_of_players,
    trial: payload.trial,
    ...(payload.mode_name_en != null ? { mode_name_en: payload.mode_name_en } : {}),
    ...(payload.imposter_count != null ? { imposter_count: payload.imposter_count } : {}),
    ...(payload.team_a_name != null ? { team_a_name: payload.team_a_name } : {}),
    ...(payload.team_b_name != null ? { team_b_name: payload.team_b_name } : {}),
    ...(payload.dare_mode != null ? { dare_mode: payload.dare_mode } : {}),
    ...(payload.dare_deck_kind != null ? { dare_deck_kind: payload.dare_deck_kind } : {}),
    ...(payload.seconds_per_turn !== undefined ? { seconds_per_turn: payload.seconds_per_turn } : {}),
    ...(payload.deck_size != null ? { deck_size: payload.deck_size } : {}),
    ...(payload.guest_join_count != null ? { guest_join_count: payload.guest_join_count } : {}),
    ...(payload.room_code_present != null ? { room_code_present: payload.room_code_present } : {}),
  });
}

/** Payload for {@link MixpanelEvents.PARTY_GAME_COMPLETED}. */
export type PartyGameCompletedPayload = {
  /** Party shell id: imposter, guess, act, who, fast, category, challenge, bluff. */
  shell_id: string;
  trial: boolean;
  /** Session duration measured client-side from round start to result render. */
  duration_seconds: number;
  /** English display name for the mode (optional). */
  mode_name_en?: string;
};

export function trackPartyGameCompleted(payload: PartyGameCompletedPayload): void {
  trackEvent(MixpanelEvents.PARTY_GAME_COMPLETED, {
    shell_id: payload.shell_id,
    trial: payload.trial,
    duration_seconds: payload.duration_seconds,
    ...(payload.mode_name_en != null ? { mode_name_en: payload.mode_name_en } : {}),
  });
}

/**
 * Reset identity on logout; SDK stays active for a fresh anonymous distinct_id.
 */
export const resetMixpanel = (): void => {
  if (!isMixpanelRuntimeEnabled) {
    return;
  }
  if (!MIXPANEL_TOKEN || !isInitialized) {
    return;
  }
  mixpanel.reset();
  resetTiktokPixel();
};

export const setUserProperties = (properties: Record<string, unknown>): void => {
  if (!isMixpanelRuntimeEnabled) {
    return;
  }
  if (!ensureMixpanelSdk()) {
    return;
  }

  mixpanel.people.set(properties);
};

export const MixpanelEvents = {
  // Authentication
  SIGN_UP: "Sign Up",
  LOGIN: "Login",
  LOGOUT: "Logout",
  LOGIN_FAILED: "Login Failed",
  SIGN_UP_FAILED: "Sign Up Failed",
  LOGIN_PAGE_VIEWED: "Login Page Viewed",
  LOGIN_SUBMIT: "Login Submit",
  SIGNUP_PAGE_VIEWED: "Sign Up Page Viewed",
  SIGNUP_SUBMIT: "Sign Up Submit",
  AUTH_GATE_REDIRECT: "Auth Gate Redirect",
  FORGOT_PASSWORD_PAGE_VIEWED: "Forgot Password Page Viewed",
  FORGOT_PASSWORD_SUBMIT: "Forgot Password Submit",
  FORGOT_PASSWORD_EMAIL_SENT: "Forgot Password Email Sent",
  FORGOT_PASSWORD_FAILED: "Forgot Password Failed",
  FORGOT_PASSWORD_CONTACT_LOGGED: "Forgot Password Contact Logged",
  FORGOT_PASSWORD_CONTACT_LOG_FAILED: "Forgot Password Contact Log Failed",

  // Game Creation
  GAME_CREATED: "Game Created",
  GAME_CREATION_FAILED: "Game Creation Failed",
  GAME_MODE_SELECTED: "Game Mode Selected",
  CATEGORY_SELECTED: "Category Selected",
  INSUFFICIENT_CREDITS: "Insufficient Credits",

  // Team Setup
  TEAM_SETUP_COMPLETED: "Team Setup Completed",
  TEAM_COLOR_SELECTED: "Team Color Selected",
  PLAYER_COUNT_CHANGED: "Player Count Changed",

  // Game Board
  GAME_STARTED: "Game Started",
  GAME_CONTINUED: "Game Continued",
  CONTENT_SELECTED: "Content Selected",
  TURN_SWITCHED: "Turn Switched",
  POINTS_ASSIGNED: "Points Assigned",
  TIMER_STARTED: "Timer Started",
  TIMER_PAUSED: "Timer Paused",
  TIMER_RESET: "Timer Reset",
  GAME_EXITED: "Game Exited",
  GAME_COMPLETED: "Game Completed",

  // Purchase
  PURCHASE_INITIATED: "Purchase Initiated",
  PURCHASE_COMPLETED: "Purchase Completed",
  PURCHASE_FAILED: "Purchase Failed",
  PAYMENT_RESULT_SUCCESS: "Payment Result Success",
  PAYMENT_RESULT_FAILED: "Payment Result Failed",
  COUPON_APPLIED: "Coupon Applied",
  COUPON_APPLY_FAILED: "Coupon Apply Failed",
  DISCOUNT_CODE_APPLIED: "Discount Code Applied",
  DISCOUNT_CODE_APPLY_FAILED: "Discount Code Apply Failed",
  PURCHASE_OPTION_SELECTED: "Purchase Option Selected",

  // Profile
  PROFILE_UPDATED: "Profile Updated",
  PASSWORD_CHANGED: "Password Changed",
  ACCOUNT_DELETED: "Account Deleted",
  PASSWORD_CHANGE_FAILED: "Password Change Failed",
  PROFILE_UPDATE_FAILED: "Profile Update Failed",

  // Onboarding
  ONBOARDING_COMPLETED: "Onboarding Completed",
  ONBOARDING_SKIPPED: "Onboarding Skipped",

  // Navigation
  PAGE_VIEWED: "Page Viewed",
  LANGUAGE_CHANGED: "Language Changed",

  // Modals & UI Interactions
  HOW_TO_PLAY_OPENED: "How to Play Opened",
  RESULTS_CONFIRMATION_SHOWN: "Results Confirmation Shown",
  RESULTS_CONFIRMED: "Results Confirmed",
  RESULTS_CANCELLED: "Results Cancelled",

  // Errors
  ERROR_OCCURRED: "Error Occurred",

  // Party catalog & play funnel
  PARTY_MODES_VIEWED: "Party Modes Viewed",
  PARTY_GAME_CARD_CLICKED: "Party Game Card Clicked",
  PARTY_DETAIL_VIEWED: "Party Detail Viewed",
  PARTY_PLAY_CLICKED: "Party Play Clicked",
  PARTY_PLAY_LOGIN_REQUIRED: "Party Play Login Required",
  PARTY_UNLOCK_CLICKED: "Party Unlock Clicked",
  PARTY_PLAY_SHELL_VIEWED: "Party Play Shell Viewed",
  PARTY_GAME_CREDITS_BLOCKED: "Party Game Credits Blocked",
  /** User chose “Try trial” on a mode detail (may still redirect to login). */
  PARTY_TRIAL_ENTRY_CLICKED: "Party Trial Entry Clicked",
  /** Play route mounted with `?trial=1` and trial is allowed for this shell (paid-by-credit mode). */
  PARTY_TRIAL_SESSION_STARTED: "Party Trial Session Started",
  /**
   * A party mode round actually began (API success or trial sample): categories as names,
   * headcounts, and shell id. See `docs/mixpanel-events.md`.
   */
  PARTY_GAME_STARTED: "Party Game Started",
  /** Party mode reached an end/results phase; includes client-side duration seconds. */
  PARTY_GAME_COMPLETED: "Party Game Completed",

  // Landing party CTAs (funnel / drop-off)
  LANDING_CTA_CLICKED: "Landing CTA Clicked",
  /** One-time-per-tab-session modal on home (same audience as footer App Store badge). */
  LANDING_DOWNLOAD_APP_PROMPT_SHOWN: "Landing Download App Prompt Shown",
  LANDING_DOWNLOAD_APP_PROMPT_DISMISSED: "Landing Download App Prompt Dismissed",

  // Answer Bluff — host
  PARTY_BLUFF_HOST_LOBBY_VIEWED: "Party Bluff Host Lobby Viewed",
  PARTY_BLUFF_ROOM_START_REQUESTED: "Party Bluff Room Start Requested",
  PARTY_BLUFF_ROOM_CREATED: "Party Bluff Room Created",
  PARTY_BLUFF_ROOM_START_FAILED: "Party Bluff Room Start Failed",
  PARTY_BLUFF_HOST_SESSION_STARTED: "Party Bluff Host Session Started",
  PARTY_BLUFF_JOIN_LINK_COPIED: "Party Bluff Join Link Copied",
  PARTY_BLUFF_HOST_ROOM_LEFT: "Party Bluff Host Room Left",
  PARTY_BLUFF_HOST_ANSWER_SUBMITTED: "Party Bluff Host Answer Submitted",
  PARTY_BLUFF_HOST_ANSWER_AUTO_SUBMITTED: "Party Bluff Host Answer Auto Submitted",
  PARTY_BLUFF_HOST_VOTE_SUBMITTED: "Party Bluff Host Vote Submitted",

  // Answer Bluff — guest join page
  PARTY_BLUFF_JOIN_PAGE_VIEWED: "Party Bluff Join Page Viewed",
  PARTY_BLUFF_GUEST_CONNECT_ATTEMPTED: "Party Bluff Guest Connect Attempted",
  PARTY_BLUFF_GUEST_WS_OPENED: "Party Bluff Guest WebSocket Opened",
  PARTY_BLUFF_GUEST_WS_ERROR: "Party Bluff Guest WebSocket Error",
  PARTY_BLUFF_GUEST_WS_CLOSED: "Party Bluff Guest WebSocket Closed",
  PARTY_BLUFF_GUEST_JOIN_SENT: "Party Bluff Guest Join Sent",
  PARTY_BLUFF_GUEST_ANSWER_SUBMITTED: "Party Bluff Guest Answer Submitted",
  PARTY_BLUFF_GUEST_ANSWER_AUTO_SUBMITTED: "Party Bluff Guest Answer Auto Submitted",
  PARTY_BLUFF_GUEST_VOTE_SUBMITTED: "Party Bluff Guest Vote Submitted",

  // Static / support pages
  CONTACT_PAGE_VIEWED: "Contact Page Viewed",
  CONTACT_FORM_SUBMITTED: "Contact Form Submitted",
  CONTACT_FORM_FAILED: "Contact Form Failed",
  LEGAL_PAGE_VIEWED: "Legal Page Viewed",
  IMPOSTER_SHARE_PAGE_VIEWED: "Imposter Share Page Viewed",
} as const;
